import { KEYCLOAK_CLIENT_ID, STORAGE_TOKENS, TOKEN_ENDPOINT } from '../config';

// Authentification par "Resource Owner Password Credentials" (grant_type=password) contre
// Keycloak, réalm sip-realm : c'est ce qui permet une page de connexion ENTIÈREMENT
// personnalisée (celle de la maquette) au lieu d'une redirection vers l'écran Keycloak hébergé.
//
// Prérequis côté client Keycloak `audit-quality-spa` (à créer une fois) :
//  - Access Type / type de client : public (pas de secret)
//  - Direct Access Grants Enabled : ON — c'est LUI qui autorise ce grant, sans lui Keycloak
//    répond 400 "unauthorized_client / Client not allowed for direct access grants"
//  - Full scope allowed : ON — sinon le token ne porte pas resource_access["sip-api"].roles et
//    le backend renvoie 403 sur tout /api/**, alors que le login a réussi (piège déjà documenté
//    pour beaujeans-spa dans le CLAUDE.md de sipconfection-frontend)
//  - Web Origins : l'origine de cette PWA (https://audit-quality.bicadev.com, http://localhost:3000
//    en dev) — le navigateur appelle le endpoint token en direct, Keycloak doit répondre le
//    bon en-tête CORS
//
// Pas de PKCE ici : ce grant n'utilise pas de redirection, PKCE ne s'applique qu'au Authorization
// Code Flow. La sécurité repose sur le canal HTTPS + la brièveté du token + le refresh_token.

function persistTokens(tokens) {
  if (!tokens) {
    localStorage.removeItem(STORAGE_TOKENS);
    return;
  }
  localStorage.setItem(STORAGE_TOKENS, JSON.stringify(tokens));
}

export function readPersistedTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_TOKENS);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

async function tokenRequest(body) {
  const params = new URLSearchParams({ client_id: KEYCLOAK_CLIENT_ID, ...body });
  const response = await fetch(TOKEN_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });

  if (!response.ok) {
    let description = null;
    try {
      const data = await response.json();
      description = data.error_description || data.error || null;
    } catch {
      // corps non JSON : on retombe sur le statut HTTP
    }
    const err = new Error(description || `Échec d'authentification (HTTP ${response.status})`);
    err.status = response.status;
    err.keycloakError = description;
    throw err;
  }

  const data = await response.json();
  const tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    obtainedAt: Date.now(),
    expiresInMs: (data.expires_in || 60) * 1000,
  };
  persistTokens(tokens);
  return tokens;
}

/** Identifiant/mot de passe → jeu de jetons. Message d'erreur exploitable en cas d'échec. */
export function login(username, password) {
  return tokenRequest({ grant_type: 'password', username, password });
}

/** Renouvelle l'access token à partir du refresh token. */
export function refresh(refreshToken) {
  return tokenRequest({ grant_type: 'refresh_token', refresh_token: refreshToken });
}

/** Révoque la session côté Keycloak (best-effort : un échec réseau ne doit jamais bloquer la sortie). */
export async function logout(refreshToken) {
  persistTokens(null);
  if (!refreshToken) return;
  try {
    await fetch(
      `${TOKEN_ENDPOINT.replace('/token', '/logout')}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ client_id: KEYCLOAK_CLIENT_ID, refresh_token: refreshToken }).toString(),
      }
    );
  } catch {
    // hors-ligne au moment de la déconnexion : la session Keycloak expirera d'elle-même
  }
}
